import api from '../api';
import { getCurrentUserId, isAuthenticated } from '../utils/auth';

// Stories API endpoints
const storyAPI = {
  // Get all stories with pagination
  getStories: (params = {}) => 
    api.get("/community/stories", { params }),

  // Get single story by ID
  getStoryById: (storyId) => 
    api.get(`/community/story/${storyId}`),

  // Create new story
  createStory: (formData) => 
    api.post("/community/story", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),

  // Update existing story
  updateStory: (storyId, updatedData) => 
    api.put(`/community/story/${storyId}`, updatedData, {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    }),

  // Delete story
  deleteStory: (storyId) => 
    api.delete(`/community/story/${storyId}`, {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    }),

  // Get stories by user
  getUserStories: (userId, params = {}) => 
    api.get(`/community/stories/user/${userId}`, { params }),
};

// Reviews API endpoints  
const reviewAPI = {
  // Get all reviews with pagination
  getReviews: (params = {}) => 
    api.get("/community/reviews", { params }),

  // Get single review by ID
  getReviewById: (reviewId) => 
    api.get(`/community/review/${reviewId}`),

  // Create new review
  createReview: (reviewData) => 
    api.post("/community/review", reviewData, {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    }),

  // Update existing review
  updateReview: (reviewId, updatedData) => 
    api.put(`/community/review/${reviewId}`, updatedData, {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    }),

  // Delete review
  deleteReview: (reviewId) => 
    api.delete(`/community/review/${reviewId}`, {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    }),

  // Add comment to review
  addComment: (reviewId, comment) => 
    api.post("/community/review/comment", 
      { reviewId, comment }, 
      {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem("token")}` 
        }
      }
    ),

  // Get reviews by user
  getUserReviews: (userId, params = {}) => 
    api.get(`/community/reviews/user/${userId}`, { params }),
};

// Presentations API endpoints
const presentationAPI = {
  // Get all presentations
  getPresentations: (params = {}) => 
    api.get("/community/presentations", { params }),
};

// User management API endpoints
const userAPI = {
  // Get current user profile
  getCurrentUser: () => {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('No user ID found in token');
    }
    return api.get(`/users/${userId}`);
  },

  // Get user by ID
  getUserById: (userId) => 
    api.get(`/users/${userId}`),
};

// High-level service functions with error handling and data transformation
const communityService = {
  // Stories management
  async fetchStories(page = 1, limit = 10) {
    try {
      const params = { page, limit };
      const response = await storyAPI.getStories(params);
      return {
        success: true,
        stories: response.data.stories || [],
        total: response.data.total || 0,
        pagination: {
          page,
          limit,
          total: response.data.total || 0,
          hasMore: (page * limit) < (response.data.total || 0)
        }
      };
    } catch (error) {
      console.error("Error fetching stories:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch stories",
        stories: [],
        total: 0
      };
    }
  },

  async createStory(storyData) {
    try {
      const formData = new FormData();
      formData.append("title", storyData.title);
      formData.append("name", storyData.name || "Anonymous");
      formData.append("content", storyData.content);
      if (storyData.image) {
        formData.append("image", storyData.image);
      }

      const response = await storyAPI.createStory(formData);
      return {
        success: true,
        story: response.data,
        message: "Story created successfully!"
      };
    } catch (error) {
      console.error("Error creating story:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to create story"
      };
    }
  },

  async updateStory(storyId, updatedData) {
    try {
      await storyAPI.updateStory(storyId, updatedData);
      return {
        success: true,
        message: "Story updated successfully!"
      };
    } catch (error) {
      console.error("Error updating story:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update story"
      };
    }
  },

  async deleteStory(storyId) {
    try {
      await storyAPI.deleteStory(storyId);
      return {
        success: true,
        message: "Story deleted successfully!"
      };
    } catch (error) {
      console.error("Error deleting story:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete story"
      };
    }
  },

  // Reviews management
  async fetchReviews(page = 1, limit = 10) {
    try {
      const params = { page, limit };
      const response = await reviewAPI.getReviews(params);
      
      // Transform reviews data for consistent format
      const formattedReviews = (response.data || []).map((review) => ({
        id: review.id,
        rating: review.rating,
        text: review.feedback,
        date: new Date(review.createdAt).toLocaleDateString(),
        user: review.username || review.email || "Anonymous",
        avatar: `https://api.dicebear.com/6.x/initials/svg?seed=${review.username || review.email}`,
        comments: review.comments || [],
        userId: review.userId,
        createdAt: review.createdAt,
        updatedAt: review.updatedAt
      }));

      return {
        success: true,
        reviews: formattedReviews,
        total: response.data.length || 0,
        pagination: {
          page,
          limit,
          total: response.data.length || 0,
          hasMore: false // API doesn't return total count yet
        }
      };
    } catch (error) {
      console.error("Error fetching reviews:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch reviews",
        reviews: [],
        total: 0
      };
    }
  },

  async createReview(reviewData) {
    try {
      const response = await reviewAPI.createReview(reviewData);
      return {
        success: true,
        review: response.data,
        message: "Review submitted successfully!"
      };
    } catch (error) {
      console.error("Error creating review:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to submit review"
      };
    }
  },

  async updateReview(reviewId, updatedData) {
    try {
      await reviewAPI.updateReview(reviewId, updatedData);
      return {
        success: true,
        message: "Review updated successfully!"
      };
    } catch (error) {
      console.error("Error updating review:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update review"
      };
    }
  },

  async deleteReview(reviewId) {
    try {
      await reviewAPI.deleteReview(reviewId);
      return {
        success: true,
        message: "Review deleted successfully!"
      };
    } catch (error) {
      console.error("Error deleting review:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete review"
      };
    }
  },

  async addComment(reviewId, comment) {
    try {
      await reviewAPI.addComment(reviewId, comment);
      return {
        success: true,
        message: "Comment added successfully!"
      };
    } catch (error) {
      console.error("Error adding comment:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to add comment"
      };
    }
  },

  // User management
  async getCurrentUser() {
    try {
      const response = await userAPI.getCurrentUser();
      return {
        success: true,
        user: response.data
      };
    } catch (error) {
      console.error("Error fetching current user:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch user data",
        user: null
      };
    }
  },

  // Fetch presentations
  async fetchPresentations(page = 1, limit = 10) {
    try {
      const response = await presentationAPI.getPresentations({ page, limit });
      return {
        success: true,
        presentations: response.data.presentations,
        total: response.data.total
      };
    } catch (error) {
      console.error("Error fetching presentations:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch presentations",
        presentations: [],
        total: 0
      };
    }
  },

  // Utility functions
  isAuthenticated() {
    return isAuthenticated();
  },

  canEdit(item, currentUser) {
    if (!currentUser || !item) return false;
    return (
      currentUser.email === item.email || 
      currentUser.id === item.userId ||
      currentUser.role === 'admin'
    );
  },

  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  },

  generateAvatar(seed) {
    return `https://api.dicebear.com/6.x/initials/svg?seed=${seed}`;
  }
};

// Export individual APIs for granular access if needed
export { storyAPI, reviewAPI, presentationAPI, userAPI };

// Export the main service as default
export default communityService;